// Enhanced AuthContext with secure token management
import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';
import { trackUserLogin, trackUserSignup } from '../utils/gtmEvents';
import { trackAfgloLead } from '../utils/afglo';
import { referralService, referralHelpers } from '../services/referralService';

// Create the context
export const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Module-level flag to prevent multiple auth redirects
// (persists across component remounts during page navigation)
let isAuthRedirectInProgress = false;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sync user.id to localStorage whenever user changes
  // Only SET on login — never remove here. Transient null states (auth refresh,
  // component remount) would wipe user_id and break in-flight API calls like
  // Stripe payment intent creation. Removal happens only in signOut().
  useEffect(() => {
    if (user && user.id) {
      localStorage.setItem('user_id', user.id);
    }
  }, [user]);

  // Initialize: try to restore session from refresh token cookie
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing auth context with token manager...');

        // Check for temp_auth_token cookie (from Apple form_post callback)
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        // Debug: Log all cookies
        console.log('🍪 All cookies:', document.cookie);

        const tempToken = getCookie('temp_auth_token');
        console.log('🔍 Checking for temp_auth_token:', tempToken ? 'FOUND' : 'NOT FOUND');

        if (tempToken) {
          console.log('✅ Found temp_auth_token from Apple callback');
          console.log('📝 Token preview:', tempToken.substring(0, 50) + '...');

          // Store in localStorage
          localStorage.setItem('auth_token', tempToken);

          // Clear the temp cookie (must include domain if cookie was set with domain)
          // Try multiple approaches to ensure cookie is cleared
          const hostname = window.location.hostname;
          const domainParts = hostname.split('.');
          const baseDomain = domainParts.length >= 2 ? `.${domainParts.slice(-2).join('.')}` : '';

          // Clear cookie for current domain
          document.cookie = 'temp_auth_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';

          // Clear cookie for base domain (e.g., .jetogo.com)
          if (baseDomain) {
            document.cookie = `temp_auth_token=; Path=/; Domain=${baseDomain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
          }

          console.log('✅ Token transferred to localStorage and cookie cleared');
          console.log('🗑️ Attempted to clear cookie for domains:', hostname, baseDomain || 'none');

          // Verify token was saved to localStorage
          const savedToken = localStorage.getItem('auth_token');
          console.log('✔️ Verify localStorage has token:', savedToken ? 'YES' : 'NO');
        } else {
          console.log('⚠️ No temp_auth_token cookie found');
        }

        // Check localStorage for auth_token
        const hasToken = localStorage.getItem('auth_token');
        console.log('🔑 localStorage auth_token:', hasToken ? 'EXISTS' : 'MISSING');

        const isLoggedIn = await authService.isAuthenticated();
        console.log('✅ Authentication check:', isLoggedIn ? 'LOGGED IN' : 'NOT LOGGED IN');

        if (isLoggedIn) {
          console.log('📥 Fetching user data...');
          const userData = await authService.getCurrentUser();
          console.log('✅ User data fetched:', {
            email: userData.email,
            id: userData.id,
            status: userData.status,
            has_active_subscriptions: userData.has_active_subscriptions,
            current_plan_type: userData.current_plan_type
          });

          // Check if user is pending approval
          if (userData.status === 'pending') {
            // Avoid infinite redirect loop - only redirect if not already on pending page
            if (window.location.pathname !== '/auth/pending-approval') {
              console.log('⚠️ User is pending approval, redirecting...');
              window.location.href = '/auth/pending-approval';
              return;
            }
            // If already on pending page, just set user and continue
            setUser(userData);
            setLoading(false);
            return;
          }

          setUser(userData);
        } else {
          console.log('⚠️ User is not authenticated');
        }
      } catch (err) {
        console.error('❌ Error initializing auth:', err);
        setError('Failed to initialize authentication');

        // Clear any stale tokens
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_token');
      } finally {
        setLoading(false);
        console.log('✅ Auth initialization complete');
      }
    };

    initializeAuth();
  }, []);

  // Listen for auth:logout events from token manager (e.g., when token refresh fails)
  useEffect(() => {
    const handleAuthLogout = (event) => {
      console.log('🚪 Auth logout event received:', event.detail);

      // Prevent multiple redirects using module-level flag
      if (isAuthRedirectInProgress) {
        console.log('⏭️ Redirect already in progress, skipping...');
        return;
      }

      // Check if already on login page
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/login')) {
        console.log('⏭️ Already on login page, skipping redirect...');
        return;
      }

      // Set flag before any async operations
      isAuthRedirectInProgress = true;

      // Clear user state
      setUser(null);

      // Clear tokens and user identity
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_id');

      // Redirect to login page with return URL (preserve query string so e.g. ?sid= survives)
      const fullPath = currentPath + window.location.search;
      const returnUrl = `?returnUrl=${encodeURIComponent(fullPath)}`;
      console.log('⏩ Redirecting to login...');
      window.location.replace(`/login${returnUrl}`);
    };

    // Reset flag when component mounts (fresh page load)
    // This allows redirect on subsequent token revocations
    if (window.location.pathname.startsWith('/login')) {
      isAuthRedirectInProgress = false;
    }

    // Add event listener
    window.addEventListener('auth:logout', handleAuthLogout);

    // Cleanup
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  // Helper to handle authentication process
  const processAuthentication = async (provider, tokenOrCode, state = null) => {
    setLoading(true);
    setError(null);
    
    try {
      let userData;
      
      // Process based on provider
      switch (provider) {
        case 'google':
          userData = await authService.handleGoogleCallback(tokenOrCode, state);
          break;
        case 'linkedin':
          userData = await authService.handleLinkedInCallback(tokenOrCode, state);
          break;
        case 'facebook':
          userData = await authService.handleFacebookCallback(tokenOrCode, state);
          break;
        case 'apple':
          userData = await authService.handleAppleCallback(tokenOrCode);
          break;
        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }
      
      console.log(`${provider} authentication successful, user data:`, userData);

      // Check if user is pending approval
      if (userData.status === 'pending') {
        setUser(userData);
        // Avoid infinite redirect loop
        if (window.location.pathname !== '/auth/pending-approval') {
          console.log('⚠️ User is pending approval, redirecting...');
          window.location.href = '/auth/pending-approval';
        }
        return;
      }

      setUser(userData);

      // Track login/signup in GTM
      // Check if this is a new user (created_at within last minute) or returning user
      // Note: userData may be { user: {...}, token: ... } or just the user object
      const userObj = userData.user || userData;
      const isNewUser = userObj.created_at &&
        (new Date() - new Date(userObj.created_at)) < 60000; // Within 1 minute

      if (isNewUser) {
        console.log(`📊 GTM: Tracking new user signup via ${provider}`);
        trackUserSignup(userObj.id, provider);

        // Track lead in Afglo (affiliate tracking) — deferred until SDK ready
        trackAfgloLead(userObj.email);

        // Process referral tracking for new users
        const referralData = referralHelpers.getReferralForSignup();
        if (referralData && referralData.code) {
          try {
            console.log('📊 Processing referral for new user:', referralData.code);

            // If we don't have referrerUserId from session, validate the code first
            let referrerUserId = referralData.referrerUserId;
            if (!referrerUserId) {
              const validation = await referralService.validateReferralCode(referralData.code);
              if (validation.valid) {
                referrerUserId = validation.referrerUserId;
              }
            }

            if (referrerUserId) {
              await referralService.createReferralTracking(
                referrerUserId,
                userObj.id,
                referralData.code,
                null, // IP address (optional)
                referralData.cookieSetAt
              );
              console.log('✅ Referral tracking created successfully');
            }
          } catch (err) {
            console.error('Failed to create referral tracking:', err);
            // Don't block authentication on referral error
          } finally {
            // Clear referral data after processing
            referralHelpers.clearReferralForSignup();
          }
        }
      } else {
        console.log(`📊 GTM: Tracking user login via ${provider}`);
        trackUserLogin(userObj.id, provider);
      }

      // Also fetch the complete user profile to ensure we have all data
      try {
        const fullUserData = await authService.getCurrentUser();
        console.log('Full user data fetched:', fullUserData);

        // Check again if user is pending (in case status changed)
        if (fullUserData.status === 'pending') {
          setUser(fullUserData);
          // Avoid infinite redirect loop
          if (window.location.pathname !== '/auth/pending-approval') {
            console.log('⚠️ User is pending approval (from full data), redirecting...');
            window.location.href = '/auth/pending-approval';
          }
          return;
        }

        setUser(fullUserData);
        return fullUserData;
      } catch (fetchErr) {
        console.error('Error fetching full user data:', fetchErr);
        // Return the initial userData if we can't fetch full data
        return userData;
      }
    } catch (err) {
      console.error(`Error during ${provider} authentication:`, err);
      setError(`Authentication with ${provider} failed`);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Authenticate with Google
  const authenticateWithGoogle = async (orderNumber = null) => {
    window.location.href = authService.initiateGoogleAuth(orderNumber);
  };

  // Authenticate with LinkedIn
  const authenticateWithLinkedIn = async (orderNumber = null) => {
    window.location.href = authService.initiateLinkedInAuth(orderNumber);
  };

  // Authenticate with Facebook
  const authenticateWithFacebook = async () => {
    window.location.href = authService.initiateFacebookAuth();
  };

  // Authenticate with Apple
  const authenticateWithApple = async () => {
    window.location.href = authService.initiateAppleAuth();
  };

  // Handle OAuth callback
  const handleAuthCallback = async (provider, code, state = null) => {
    return processAuthentication(provider, code, state);
  };

  // Authenticate with email and password
  const authenticateWithEmail = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.loginWithEmail(email, password);

      // Check if user is pending approval
      if (response.user.status === 'pending') {
        console.log('⚠️ User is pending approval');
        setUser(response.user);
        // Return response with pending flag for LoginPage to handle redirect
        return { ...response, pending: true };
      }

      // Check if user is suspended - still set user data for read-only access
      if (response.suspended) {
        console.log('⚠️ User is suspended, setting user data for read-only access');
        // Set user data with suspension info
        const suspendedUser = {
          ...response.user,
          is_suspended: true,
          suspension_info: {
            suspended_at: response.user.suspended_at,
            reason: response.user.suspension_reason,
          }
        };
        setUser(suspendedUser);
        // Return response with suspended flag for LoginPage to handle redirect
        return { ...response, user: suspendedUser };
      }

      // Set user data in context
      setUser(response.user);

      // Track login in GTM
      // Check if this is a new user (created_at within last minute) or returning user
      const isNewUser = response.user.created_at &&
        (new Date() - new Date(response.user.created_at)) < 60000; // Within 1 minute

      if (isNewUser) {
        console.log('📊 GTM: Tracking new user signup via email');
        trackUserSignup(response.user.id, 'email');

        // Track lead in Afglo (affiliate tracking) — deferred until SDK ready
        trackAfgloLead(response.user.email);
      } else {
        console.log('📊 GTM: Tracking user login via email');
        trackUserLogin(response.user.id, 'email');
      }

      return response;
    } catch (err) {
      console.error('Error during email authentication:', err);
      setError('Email authentication failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Request an email+password signup. Does NOT log in — the backend emails a
  // verification link; the account is created when the link is clicked.
  const registerWithEmail = async (name, email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.signupWithEmail(name, email, password);
      return response; // { success, message } — "check your email"
    } catch (err) {
      console.error('Error during email registration:', err);
      setError('Email registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Complete an email signup via the verification-link token (logs the user in)
  const verifyEmailToken = async (token) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.verifyEmail(token);

      // Backend returns a fully authenticated session
      setUser(response.user);

      // Track signup in GTM
      console.log('📊 GTM: Tracking new user signup via email');
      trackUserSignup(response.user.id, 'email');

      // Track lead in Afglo (affiliate tracking) — deferred until SDK ready
      trackAfgloLead(response.user.email);

      return response;
    } catch (err) {
      console.error('Error during email verification:', err);
      setError('Email verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    setLoading(true);

    try {
      await authService.signOut();
      localStorage.removeItem('user_id');
      setUser(null);
    } catch (err) {
      console.error('Error during sign out:', err);
      setError('Failed to sign out');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user;
  };

  // Link order with user
  const linkOrderWithUser = async (orderNumber) => {
    try {
      return await authService.linkOrderWithUser(orderNumber);
    } catch (err) {
      console.error('Error linking order with user:', err);
      setError('Failed to link order with user account');
      throw err;
    }
  };

  // Update user data in context (useful for admin login as user)
  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser({ skipCache: true });

      // Check if user is pending approval
      if (userData.status === 'pending') {
        setUser(userData);
        // Avoid infinite redirect loop
        if (window.location.pathname !== '/auth/pending-approval') {
          console.log('⚠️ User is pending approval, redirecting...');
          window.location.href = '/auth/pending-approval';
        }
        return;
      }

      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Error updating user data:', err);
      setError('Failed to update user data');
      throw err;
    }
  };

  // Alias for signOut (used on AccountSuspended page)
  const logout = signOut;

  // Verify token is still valid (checks token_invalidated_at on backend)
  // Call this on route changes to detect revoked tokens
  // Note: If token is invalid, apiService will dispatch 'auth:logout' event
  // which is handled by the event listener above - no need to redirect here
  const verifyToken = async () => {
    // Skip if no token or already loading
    if (!localStorage.getItem('auth_token') || loading) {
      return true;
    }

    try {
      // This will throw if token is revoked
      // On 401, apiService dispatches 'auth:logout' event which handles redirect
      await authService.getCurrentUser();
      return true;
    } catch (err) {
      // Error is already handled by apiService (dispatches auth:logout on 401)
      console.log('🔒 Token verification failed - auth:logout event will handle redirect');
      return false;
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    authenticateWithGoogle,
    authenticateWithLinkedIn,
    authenticateWithFacebook,
    authenticateWithApple,
    authenticateWithEmail,
    registerWithEmail,
    verifyEmailToken,
    handleAuthCallback,
    signOut,
    logout, // Alias for signOut
    linkOrderWithUser,
    refreshUser,
    verifyToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;